import * as XLSX from 'xlsx';

/**
 * Export Service - Handles all data export functionality
 * Supports exporting various data types to Excel format
 */

// Utility function to format date for export
const formatDateForExport = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Utility function to safely get nested object properties
const safeGet = (obj, path, defaultValue = 'N/A') => {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue;
    }
  }
  return current || defaultValue;
};

/**
 * Export Hospital Applications to Excel
 */
export const exportHospitalApplications = (applications, filename = 'hospital_applications') => {
  try {
    const exportData = applications.map((app, index) => ({
      'S.No': index + 1,
      'Application ID': app.applicationId || 'N/A',
      'Hospital Name': app.hospitalName || 'N/A',
      'Registration Number': app.registrationNumber || 'N/A',
      'Contact Person': safeGet(app, 'representative.name'),
      'Email': app.email || 'N/A',
      'Phone': app.phone || 'N/A',
      'Address': `${app.address || ''}, ${app.city || ''}, ${app.state || ''} - ${app.pincode || ''}`.replace(/^, |, $/, ''),
      'Number of Beds': app.numberOfBeds || 'N/A',
      'Hospital Type': app.hospitalType || 'N/A',
      'Ownership': app.ownership || 'N/A',
      'Facilities': Array.isArray(app.facilities) ? app.facilities.join(', ') : 'N/A',
      'Specialities': Array.isArray(app.specialities) ? app.specialities.join(', ') : 'N/A',
      'Status': app.status || 'N/A',
      'Submitted Date': formatDateForExport(app.submittedAt),
      'Approved Date': formatDateForExport(app.approvedAt),
      'Rejected Date': formatDateForExport(app.rejectedAt),
      'Rejection Reason': app.rejectionReason || 'N/A',
      'Approved By': app.approvedBy || 'N/A',
      'Rejected By': app.rejectedBy || 'N/A',
      'Website': app.website || 'N/A',
      'Emergency Contact': app.emergencyContact || 'N/A',
      'License Number': app.licenseNumber || 'N/A',
      'Accreditation': app.accreditation || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Application ID
      { wch: 25 },  // Hospital Name
      { wch: 20 },  // Registration Number
      { wch: 20 },  // Contact Person
      { wch: 25 },  // Email
      { wch: 15 },  // Phone
      { wch: 40 },  // Address
      { wch: 12 },  // Number of Beds
      { wch: 15 },  // Hospital Type
      { wch: 15 },  // Ownership
      { wch: 30 },  // Facilities
      { wch: 30 },  // Specialities
      { wch: 12 },  // Status
      { wch: 18 },  // Submitted Date
      { wch: 18 },  // Approved Date
      { wch: 18 },  // Rejected Date
      { wch: 30 },  // Rejection Reason
      { wch: 15 },  // Approved By
      { wch: 15 },  // Rejected By
      { wch: 20 },  // Website
      { wch: 18 },  // Emergency Contact
      { wch: 18 },  // License Number
      { wch: 15 }   // Accreditation
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Hospital Applications');
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Hospital applications exported successfully!' };
  } catch (error) {
    console.error('Error exporting hospital applications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export Lead Generator Applications to Excel
 */
export const exportLeadGeneratorApplications = (applications, filename = 'lead_generator_applications') => {
  try {
    const exportData = applications.map((app, index) => ({
      'S.No': index + 1,
      'Application ID': app.applicationId || 'N/A',
      'Entity Name': app.entityName || 'N/A',
      'Entity Type': app.entityType || 'N/A',
      'CIN Number': app.cinNumber || 'N/A',
      'GST Number': app.gstNumber || 'N/A',
      'Contact Person': safeGet(app, 'representative.name'),
      'Designation': safeGet(app, 'representative.designation'),
      'Email': app.email || 'N/A',
      'Phone': app.phone || 'N/A',
      'Address': `${app.address || ''}, ${app.city || ''}, ${app.state || ''} - ${app.pincode || ''}`.replace(/^, |, $/, ''),
      'Services Required': Array.isArray(app.servicesRequired) ? app.servicesRequired.join(', ') : 'N/A',
      'Target Audience': app.targetAudience || 'N/A',
      'Marketing Channels': Array.isArray(app.marketingChannels) ? app.marketingChannels.join(', ') : 'N/A',
      'Expected Monthly Leads': app.expectedMonthlyLeads || 'N/A',
      'Commission Structure': app.commissionStructure || 'N/A',
      'Status': app.status || 'N/A',
      'Submitted Date': formatDateForExport(app.submittedAt),
      'Approved Date': formatDateForExport(app.approvedAt),
      'Rejected Date': formatDateForExport(app.rejectedAt),
      'Rejection Reason': app.rejectionReason || 'N/A',
      'Approved By': app.approvedBy || 'N/A',
      'Rejected By': app.rejectedBy || 'N/A',
      'Website': app.website || 'N/A',
      'Years in Business': app.yearsInBusiness || 'N/A',
      'Annual Revenue': app.annualRevenue || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Application ID
      { wch: 25 },  // Entity Name
      { wch: 15 },  // Entity Type
      { wch: 18 },  // CIN Number
      { wch: 18 },  // GST Number
      { wch: 20 },  // Contact Person
      { wch: 15 },  // Designation
      { wch: 25 },  // Email
      { wch: 15 },  // Phone
      { wch: 40 },  // Address
      { wch: 30 },  // Services Required
      { wch: 20 },  // Target Audience
      { wch: 25 },  // Marketing Channels
      { wch: 20 },  // Expected Monthly Leads
      { wch: 20 },  // Commission Structure
      { wch: 12 },  // Status
      { wch: 18 },  // Submitted Date
      { wch: 18 },  // Approved Date
      { wch: 18 },  // Rejected Date
      { wch: 30 },  // Rejection Reason
      { wch: 15 },  // Approved By
      { wch: 15 },  // Rejected By
      { wch: 20 },  // Website
      { wch: 15 },  // Years in Business
      { wch: 15 }   // Annual Revenue
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Lead Generator Applications');
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Lead generator applications exported successfully!' };
  } catch (error) {
    console.error('Error exporting lead generator applications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export Patient Applications to Excel
 */
export const exportPatientApplications = (applications, filename = 'patient_applications') => {
  try {
    const exportData = applications.map((app, index) => ({
      'S.No': index + 1,
      'Application ID': app.applicationId || 'N/A',
      'Patient Name': safeGet(app, 'patient.name'),
      'Age': safeGet(app, 'patient.age'),
      'Gender': safeGet(app, 'patient.gender'),
      'Phone': safeGet(app, 'patient.phone'),
      'Email': safeGet(app, 'patient.email'),
      'Address': safeGet(app, 'patient.address'),
      'City': safeGet(app, 'patient.city'),
      'State': safeGet(app, 'patient.state'),
      'Pincode': safeGet(app, 'patient.pincode'),
      'Medical Condition': app.medicalCondition || 'N/A',
      'Treatment Required': app.treatmentRequired || 'N/A',
      'Hospital': app.hospitalName || 'N/A',
      'Doctor': app.doctorName || 'N/A',
      'Estimated Cost': app.estimatedCost || 'N/A',
      'Insurance Provider': app.insuranceProvider || 'N/A',
      'Policy Number': app.policyNumber || 'N/A',
      'Status': app.status || 'N/A',
      'Current Stage': safeGet(app, 'workflow.currentStage'),
      'Submitted Date': formatDateForExport(app.submittedAt),
      'Last Updated': formatDateForExport(app.updatedAt),
      'Assigned Doctor': app.assignedDoctor || 'N/A',
      'Review Status': safeGet(app, 'workflow.medicalVerification.status'),
      'KYP Status': safeGet(app, 'workflow.medicalVerification.kypStatus'),
      'Approval Status': safeGet(app, 'workflow.finalApproval.status'),
      'Discharge Status': safeGet(app, 'workflow.discharge.status'),
      'Comments': app.comments || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Application ID
      { wch: 20 },  // Patient Name
      { wch: 8 },   // Age
      { wch: 10 },  // Gender
      { wch: 15 },  // Phone
      { wch: 25 },  // Email
      { wch: 30 },  // Address
      { wch: 15 },  // City
      { wch: 15 },  // State
      { wch: 10 },  // Pincode
      { wch: 25 },  // Medical Condition
      { wch: 25 },  // Treatment Required
      { wch: 20 },  // Hospital
      { wch: 20 },  // Doctor
      { wch: 15 },  // Estimated Cost
      { wch: 20 },  // Insurance Provider
      { wch: 15 },  // Policy Number
      { wch: 12 },  // Status
      { wch: 15 },  // Current Stage
      { wch: 18 },  // Submitted Date
      { wch: 18 },  // Last Updated
      { wch: 20 },  // Assigned Doctor
      { wch: 15 },  // Review Status
      { wch: 12 },  // KYP Status
      { wch: 15 },  // Approval Status
      { wch: 15 },  // Discharge Status
      { wch: 30 }   // Comments
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Patient Applications');
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Patient applications exported successfully!' };
  } catch (error) {
    console.error('Error exporting patient applications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export User Management Data to Excel
 */
export const exportUserManagementData = (users, filename = 'user_management') => {
  try {
    const exportData = users.map((user, index) => ({
      'S.No': index + 1,
      'User ID': user.uid || 'N/A',
      'Name': user.name || 'N/A',
      'Email': user.email || 'N/A',
      'Phone': user.phone || 'N/A',
      'Role': user.role || 'N/A',
      'Department': user.department || 'N/A',
      'Designation': user.designation || 'N/A',
      'Employee ID': user.employeeId || 'N/A',
      'Status': user.isActive ? 'Active' : 'Inactive',
      'Created Date': formatDateForExport(user.createdAt),
      'Last Login': formatDateForExport(user.lastLogin),
      'Created By': user.createdBy || 'N/A',
      'Hospital': user.hospitalName || 'N/A',
      'Specialization': user.specialization || 'N/A',
      'License Number': user.licenseNumber || 'N/A',
      'Experience': user.experience || 'N/A',
      'Permissions': Array.isArray(user.permissions) ? user.permissions.join(', ') : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // User ID
      { wch: 20 },  // Name
      { wch: 25 },  // Email
      { wch: 15 },  // Phone
      { wch: 15 },  // Role
      { wch: 15 },  // Department
      { wch: 15 },  // Designation
      { wch: 15 },  // Employee ID
      { wch: 10 },  // Status
      { wch: 18 },  // Created Date
      { wch: 18 },  // Last Login
      { wch: 15 },  // Created By
      { wch: 20 },  // Hospital
      { wch: 20 },  // Specialization
      { wch: 15 },  // License Number
      { wch: 12 },  // Experience
      { wch: 30 }   // Permissions
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Management');
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'User management data exported successfully!' };
  } catch (error) {
    console.error('Error exporting user management data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export Dashboard Statistics to Excel
 */
export const exportDashboardStats = (stats, additionalData = {}, filename = 'dashboard_statistics') => {
  try {
    // Create multiple sheets for different statistics
    const workbook = XLSX.utils.book_new();

    // Main Statistics Sheet
    const statsData = [
      { Metric: 'Total Applications', Value: stats.totalApplications || 0 },
      { Metric: 'Pending Applications', Value: stats.pendingApplications || 0 },
      { Metric: 'Approved Hospitals', Value: stats.approvedHospitals || 0 },
      { Metric: 'Rejected Applications', Value: stats.rejectedApplications || 0 },
      { Metric: 'This Month Applications', Value: stats.thisMonthApplications || 0 },
      { Metric: 'Average Approval Time (days)', Value: stats.averageApprovalTime || 0 },
      { Metric: 'Active Users', Value: stats.activeUsers || 0 },
      { Metric: 'Total Doctors', Value: stats.totalDoctors || 0 },
      { Metric: 'Total System Admins', Value: stats.totalSystemAdmins || 0 },
      { Metric: 'Export Generated On', Value: new Date().toLocaleString('en-IN') }
    ];

    const statsSheet = XLSX.utils.json_to_sheet(statsData);
    statsSheet['!cols'] = [{ wch: 30 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, statsSheet, 'Statistics');

    // Add additional data sheets if provided
    if (additionalData.recentActivities) {
      const activitiesSheet = XLSX.utils.json_to_sheet(additionalData.recentActivities);
      XLSX.utils.book_append_sheet(workbook, activitiesSheet, 'Recent Activities');
    }

    if (additionalData.monthlyTrends) {
      const trendsSheet = XLSX.utils.json_to_sheet(additionalData.monthlyTrends);
      XLSX.utils.book_append_sheet(workbook, trendsSheet, 'Monthly Trends');
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Dashboard statistics exported successfully!' };
  } catch (error) {
    console.error('Error exporting dashboard statistics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export Doctor Applications to Excel
 */
export const exportDoctorApplications = (applications, filename = 'doctor_applications') => {
  try {
    const exportData = applications.map((app, index) => ({
      'S.No': index + 1,
      'Application ID': app.applicationId || 'N/A',
      'Patient Name': safeGet(app, 'patient.name'),
      'Age': safeGet(app, 'patient.age'),
      'Gender': safeGet(app, 'patient.gender'),
      'Medical Condition': app.medicalCondition || 'N/A',
      'Treatment Required': app.treatmentRequired || 'N/A',
      'Hospital': app.hospitalName || 'N/A',
      'Estimated Cost': app.estimatedCost || 'N/A',
      'Insurance Provider': app.insuranceProvider || 'N/A',
      'Status': app.status || 'N/A',
      'Current Stage': safeGet(app, 'workflow.currentStage'),
      'Review Status': safeGet(app, 'workflow.medicalVerification.status'),
      'Assigned Date': formatDateForExport(app.assignedAt),
      'Last Activity': formatDateForExport(app.lastActivity),
      'KYP Status': safeGet(app, 'workflow.medicalVerification.kypStatus'),
      'Clarifications': safeGet(app, 'workflow.medicalVerification.clarificationsCount', 0),
      'Review Duration (hours)': app.reviewDuration || 'N/A',
      'Doctor Comments': safeGet(app, 'workflow.medicalVerification.doctorComments'),
      'Priority': app.priority || 'Normal'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Application ID
      { wch: 20 },  // Patient Name
      { wch: 8 },   // Age
      { wch: 10 },  // Gender
      { wch: 25 },  // Medical Condition
      { wch: 25 },  // Treatment Required
      { wch: 20 },  // Hospital
      { wch: 15 },  // Estimated Cost
      { wch: 20 },  // Insurance Provider
      { wch: 12 },  // Status
      { wch: 15 },  // Current Stage
      { wch: 15 },  // Review Status
      { wch: 18 },  // Assigned Date
      { wch: 18 },  // Last Activity
      { wch: 12 },  // KYP Status
      { wch: 12 },  // Clarifications
      { wch: 18 },  // Review Duration
      { wch: 30 },  // Doctor Comments
      { wch: 12 }   // Priority
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Doctor Applications');
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Doctor applications exported successfully!' };
  } catch (error) {
    console.error('Error exporting doctor applications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export Financial Data to Excel
 */
export const exportFinancialData = (data, filename = 'financial_data') => {
  try {
    const exportData = data.map((item, index) => ({
      'S.No': index + 1,
      'Application ID': item.applicationId || 'N/A',
      'Patient Name': safeGet(item, 'patient.name'),
      'Hospital': item.hospitalName || 'N/A',
      'Treatment Cost': item.treatmentCost || 'N/A',
      'Insurance Coverage': item.insuranceCoverage || 'N/A',
      'Patient Contribution': item.patientContribution || 'N/A',
      'Reimbursement Amount': item.reimbursementAmount || 'N/A',
      'Payment Status': item.paymentStatus || 'N/A',
      'Payment Date': formatDateForExport(item.paymentDate),
      'Transaction ID': item.transactionId || 'N/A',
      'Invoice Number': item.invoiceNumber || 'N/A',
      'Discharge Date': formatDateForExport(item.dischargeDate),
      'Final Settlement': item.finalSettlement || 'N/A',
      'Outstanding Amount': item.outstandingAmount || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },   // S.No
      { wch: 20 },  // Application ID
      { wch: 20 },  // Patient Name
      { wch: 20 },  // Hospital
      { wch: 15 },  // Treatment Cost
      { wch: 18 },  // Insurance Coverage
      { wch: 18 },  // Patient Contribution
      { wch: 18 },  // Reimbursement Amount
      { wch: 15 },  // Payment Status
      { wch: 18 },  // Payment Date
      { wch: 18 },  // Transaction ID
      { wch: 15 },  // Invoice Number
      { wch: 18 },  // Discharge Date
      { wch: 15 },  // Final Settlement
      { wch: 18 }   // Outstanding Amount
    ];
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Financial Data');
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return { success: true, message: 'Financial data exported successfully!' };
  } catch (error) {
    console.error('Error exporting financial data:', error);
    return { success: false, error: error.message };
  }
};

// Export all functions
export default {
  exportHospitalApplications,
  exportLeadGeneratorApplications,
  exportPatientApplications,
  exportUserManagementData,
  exportDashboardStats,
  exportDoctorApplications,
  exportFinancialData
};
